export type Result<TValue, TError = Error> =
  | Readonly<{ tag: 'ok'; value: TValue }>
  | Readonly<{ tag: 'err'; error: TError }>;

export const ok = <TValue>(value: TValue): Result<TValue, never> =>
  ({
    tag: 'ok',
    value,
  }) as const;

export const err = <TError>(error: TError): Result<never, TError> =>
  ({
    tag: 'err',
    error,
  }) as const;

export const map = <TInput, TOutput, TError>(
  result: Result<TInput, TError>,
  mapper: (value: TInput) => TOutput,
): Result<TOutput, TError> => {
  if (result.tag === 'ok') {
    return ok(mapper(result.value));
  }

  return result;
};

export const mapErr = <TValue, TError, TNextError>(
  result: Result<TValue, TError>,
  mapper: (error: TError) => TNextError,
): Result<TValue, TNextError> => {
  if (result.tag === 'ok') {
    return result;
  }

  return err(mapper(result.error));
};

export const mapOrError = <TInput, TOutput, TError>(
  result: Result<TInput, TError>,
  mapper: (value: TInput) => TOutput,
  mapThrownError: (error: unknown) => TError,
): Result<TOutput, TError> => {
  if (result.tag === 'err') {
    return result;
  }

  try {
    return ok(mapper(result.value));
  } catch (error) {
    return err(mapThrownError(error));
  }
};
